import React, { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { motion } from "framer-motion";
import { GraduationCap, Globe, Award, Music4, ArrowRight, CheckCircle2, Quote, Send, CalendarCheck, Star } from "lucide-react";
import { toast } from "sonner";
import { Navbar } from "@/components/Navbar";
import { AboutGallery } from "@/components/AboutGallery";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { api, formatApiErrorDetail } from "@/lib/api";

const HIGHLIGHTS = [
  { icon: Music4, label: "40+ years at the piano" },
  { icon: Globe, label: "Performed in 25+ countries" },
  { icon: CheckCircle2, label: "100% exam pass rate" },
  { icon: Award, label: "BA (Hons) Music · BEd · Grade 8 Piano · Advanced Jazz Piano" },
];


const ARTIST_GROUPS = [
  {
    genre: "Gospel & worship",
    names: ["Cece Winans", "Richard Smallwood", "Donnie McClurkin", "Micah Stampley", "Myron Butler", "Jonathan Nelson", "Kurt Carr", "Ann Nesby"],
  },
  {
    genre: "Soul & R&B",
    names: ["Jennifer Hudson", "Janet Jackson", "Beverley Knight", "Mica Paris", "Heather Small", "Omar", "Oleta Adams", "Peabo Bryson", "Caron Wheeler", "Patti Boulaye", "Ruby Turner", "The Isley Brothers"],
  },
  {
    genre: "Pop & chart",
    names: ["Stormzy", "Rod Stewart", "Gabrielle", "Simon Webbe"],
  },
  {
    genre: "Reggae & Soca",
    names: ["Maxi Priest", "Alison Hinds", "Aswad", "Dawn Penn", "Carol Thompson", "Janet Kay"],
  },
  {
    genre: "Blues, Jazz, Other",
    names: ["Courtney Pine", "Incognito", "Beverley Skeete"],
  },
];

// Placeholder testimonials — shown only when the admin hasn't published any.
const SAMPLE_TESTIMONIALS = [
  { quote: "JT made reading music finally click for me. Three months in and I'm sight-reading hymns at church.", name: "", role: "Adult beginner · Piano" },
  { quote: "My drum notation went from a mystery to second nature. The lessons are clear and genuinely fun.", name: "", role: "Improver · Drums" },
  { quote: "As a teacher I use The JT Hub with my whole class. The structure and progress tracking are brilliant.", name: "", role: "Secondary music teacher" },
];

const BookingForm = () => {
  const loc = useLocation();
  const [form, setForm] = useState({ name: "", email: "", message: loc.state?.prefill || "" });
  const [sent, setSent] = useState(false);
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post("/contact", form);
      setSent(true);
      toast.success("Thanks — JT will be in touch soon");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Could not send. Please try again.");
    } finally { setBusy(false); }
  };

  if (sent) {
    return (
      <div className="rounded-3xl border border-border bg-card p-8 text-center" data-testid="booking-success">
        <CalendarCheck className="mx-auto text-emerald-600 dark:text-emerald-400" size={32} />
        <p className="mt-3 font-serif text-2xl">Request received</p>
        <p className="mt-2 text-sm text-muted-foreground">Thank you, {form.name.split(" ")[0] || "there"}. JT will reply to {form.email} shortly to arrange your 1:1.</p>
      </div>
    );
  }

  return (
    <form onSubmit={submit} className="rounded-3xl border border-border bg-card p-6 sm:p-8" data-testid="booking-form">
      <div className="grid gap-4 sm:grid-cols-2">
        <div>
          <Label htmlFor="b-name" className="text-muted-foreground">Your name</Label>
          <Input id="b-name" required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="booking-name" className="mt-1 border-border bg-background" placeholder="Your name" />
        </div>
        <div>
          <Label htmlFor="b-email" className="text-muted-foreground">Email</Label>
          <Input id="b-email" type="email" required value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="booking-email" className="mt-1 border-border bg-background" placeholder="you@example.com" />
        </div>
      </div>
      <div className="mt-4">
        <Label htmlFor="b-msg" className="text-muted-foreground">What would you like to work on?</Label>
        <textarea id="b-msg" required value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} data-testid="booking-message" rows={4} maxLength={2000}
          className="mt-1 w-full rounded-lg border border-border bg-background px-3 py-2 text-sm focus:border-primary focus:outline-none"
          placeholder="Instrument, your level, and what you'd love to achieve…" />
      </div>
      <Button type="submit" disabled={busy} data-testid="booking-submit" className="mt-4 rounded-full bg-primary px-6 py-6 text-foreground hover:bg-primary/90">
        <Send size={16} className="mr-2" /> {busy ? "Sending…" : "Request a 1:1 with JT"}
      </Button>
    </form>
  );
};

export default function About() {
  const location = useLocation();
  const [testimonials, setTestimonials] = useState(null);
  useEffect(() => {
    if (location.hash === "#gallery") {
      setTimeout(() => document.getElementById("gallery")?.scrollIntoView({ behavior: "smooth" }), 300);
    }
    if (location.hash === "#booking" || location.state?.prefill) {
      setTimeout(() => document.getElementById("booking")?.scrollIntoView({ behavior: "smooth" }), 300);
    }
  }, [location]);
  useEffect(() => {
    api.get("/testimonials")
      .then(({ data }) => setTestimonials(data.testimonials?.length ? data.testimonials : SAMPLE_TESTIMONIALS))
      .catch(() => setTestimonials(SAMPLE_TESTIMONIALS));
  }, []);
  const quotes = testimonials || SAMPLE_TESTIMONIALS;
  const isSample = !testimonials || testimonials === SAMPLE_TESTIMONIALS;
  return (
    <div className="min-h-screen text-foreground">
      <Navbar />

      <section className="mx-auto max-w-6xl px-4 sm:px-8 py-12 lg:py-20">
        <div className="grid items-center gap-10 lg:grid-cols-2">
          <motion.div initial={{ opacity: 0, y: 18 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }}>
            <p className="font-mono text-xs uppercase tracking-[0.25em] text-primary">Meet the tutor</p>
            <h1 className="mt-4 font-serif text-4xl leading-tight tracking-tight sm:text-5xl lg:text-6xl">
              Hello, I'm Jason Thompson.
            </h1>
            <p className="mt-6 text-base leading-relaxed text-muted-foreground sm:text-lg">
              I've spent over 40 years at the piano and a lifetime in music — from international stages in
              more than 25 countries to the classroom. The JT Hub is where I share the shortcut I wish I'd had:
              learning to <span className="font-medium text-foreground">read music</span> clearly, confidently and quickly.
            </p>
            <p className="mt-4 text-base leading-relaxed text-muted-foreground">
              Whether you sing, drum, play guitar or piano — beginner or improver — my job is to make sure the dots on the
              page make sense, so you can play or sing it – even improvise and truly understand the music in front of you.
            </p>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, scale: 0.96 }} animate={{ opacity: 1, scale: 1 }} transition={{ duration: 0.5, delay: 0.1 }}
            className="relative"
          >
            <div className="overflow-hidden rounded-3xl border border-border bg-white shadow-xl shadow-amber-900/10">
              <img
                src="/media/jt-pose-1.jpg"
                alt="Jason Thompson smiling in a brown suit"
                data-testid="about-tutor-photo"
                className="h-full w-full object-cover object-top"
              />
            </div>
            <div className="absolute -bottom-5 -left-4 rounded-2xl bg-secondary px-5 py-3 text-foreground shadow-lg">
              <p className="font-serif text-lg">JT</p>
              <p className="font-mono text-[10px] uppercase tracking-widest text-primary">Founder · The JT Hub</p>
            </div>
          </motion.div>
        </div>
      </section>

      <section className="border-y border-border bg-card">
        <div className="mx-auto grid max-w-6xl gap-4 px-4 sm:px-8 py-10 sm:grid-cols-2 lg:grid-cols-4">
          {HIGHLIGHTS.map((h, i) => (
            <motion.div
              key={h.label} initial={{ opacity: 0, y: 12 }} whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }} transition={{ delay: i * 0.05 }}
              className="rounded-2xl border border-border bg-background p-5"
              data-testid={`about-highlight-${i}`}
            >
              <span className="flex h-10 w-10 items-center justify-center rounded-xl bg-primary/10 text-primary"><h.icon size={18} /></span>
              <p className="mt-3 text-sm font-medium text-foreground">{h.label}</p>
            </motion.div>
          ))}
        </div>
      </section>

      <section className="mx-auto max-w-6xl px-4 sm:px-8 py-14">
        <div className="grid gap-10 lg:grid-cols-3">
          <div className="lg:col-span-2">
            <h2 className="font-serif text-2xl">A life in music</h2>
            <div className="mt-4 space-y-4 text-muted-foreground">
              <p>
                My career has taken me from international stages to television studios and teaching rooms. I've had the
                privilege of working alongside chart-toppers and legends alike — accompanying, arranging and directing
                music for some remarkable artists.
              </p>
              <p data-testid="about-famous-moments">
                I've performed for the Pope, and the former President of the United States, Bill Clinton. I also played
                for the wedding of Rupert Murdoch's granddaughter, where he was in attendance. I shared a table with
                Michael Douglas at a charity event hosted by Sir Richard Branson.
              </p>
              <p>
                Much of my heart is in <span className="font-medium text-foreground">gospel and worship music</span> —
                I've played for and shared the stage with gospel greats including Cece Winans, Richard Smallwood,
                Donnie McClurkin, Micah Stampley, Myron Butler, Ann Nesby, Jonathan Nelson and Kurt Carr, and I understand exactly how a
                church musician needs to read, feel and support a moment of worship.
              </p>
              <p>
                Alongside performing, I've taught music at GCSE, GNVQ, BTEC, A-level Music Tech and diploma level. I hold a <span className="font-medium text-foreground">BA (Hons) in Music</span> and a
                <span className="font-medium text-foreground"> BEd in Music Education</span>, a <span className="font-medium text-foreground" data-testid="about-grade8">Grade 8 in Piano</span> and
                <span className="font-medium text-foreground"> Advanced Jazz Piano from Goldsmiths, University of London</span>{" "}
                — and a <span className="font-medium text-foreground" data-testid="about-pass-rate">100% pass rate</span> for the students I've entered for examinations in 30 years with ABRSM (The Associated Board of the Royal Schools of Music).
              </p>
              <p>
                I believe reading music shouldn't be a mystery reserved for the few. With the right tips and a clear path,
                anyone can get there — and it opens up a whole new dimension of playing.
              </p>
            </div>

            <h3 className="mt-10 font-serif text-xl">Teaching philosophy</h3>
            <p className="mt-3 text-muted-foreground">
              Practical, patient and efficient. I focus on the shortcuts that actually work, build genuine confidence
              at the staff, and keep you supported the whole way — structured lessons, playable examples and answers
              when you're stuck.
            </p>
          </div>

          <div>
            <h3 className="font-serif text-xl">Met behind stage, shared the stage or studio with, or played for…</h3>
            <div className="mt-4 space-y-4" data-testid="about-artists">
              {ARTIST_GROUPS.map((g) => (
                <div key={g.genre} data-testid={`artist-group-${g.genre.toLowerCase().replace(/[^a-z]+/g, "-")}`}>
                  <p className="font-mono text-[11px] uppercase tracking-widest text-primary">{g.genre}</p>
                  <div className="mt-2 flex flex-wrap gap-2">
                    {g.names.map((a) => (
                      <span key={a} className="rounded-full border border-border bg-card px-3 py-1.5 text-sm text-muted-foreground">{a}</span>
                    ))}
                  </div>
                </div>
              ))}
              <span className="inline-block rounded-full bg-primary/10 px-3 py-1.5 text-sm text-primary">& many more</span>
            </div>
          </div>
        </div>

        <AboutGallery />

        <section className="mt-16" data-testid="testimonials-section">
          <h2 className="font-serif text-2xl">What students say</h2>
          {isSample ? null : (
            <p className="mt-1 flex items-center gap-1.5 text-sm text-muted-foreground" data-testid="rating-average">
              <Star size={15} className="fill-primary text-primary" />
              <span className="font-medium text-foreground">{(quotes.reduce((a, t) => a + (t.rating || 5), 0) / quotes.length).toFixed(1)}</span>
              from {quotes.length} student{quotes.length === 1 ? "" : "s"}
            </p>
          )}
          <div className="mt-6 grid gap-4 md:grid-cols-3">
            {quotes.map((t, i) => (
              <motion.figure
                key={t.id || i} initial={{ opacity: 0, y: 12 }} whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }} transition={{ delay: i * 0.06 }}
                className="rounded-2xl border border-border bg-card p-6" data-testid={`testimonial-${i}`}
              >
                <span className="flex items-center gap-0.5">
                  {[1, 2, 3, 4, 5].map((n) => <Star key={n} size={14} className={n <= (t.rating || 5) ? "fill-primary text-primary" : "text-muted-foreground"} />)}
                </span>
                <blockquote className="mt-3 text-muted-foreground">"{t.quote}"</blockquote>
                <figcaption className="mt-4 flex items-center gap-3">
                  {t.photo_url
                    ? <img src={t.photo_url} alt={t.name} className="h-10 w-10 rounded-full object-cover" />
                    : <span className="flex h-10 w-10 items-center justify-center rounded-full bg-primary/10 font-serif text-sm text-primary">{t.name ? t.name.split(" ").map((w) => w[0]).slice(0, 2).join("") : <Quote size={16} />}</span>}
                  <span>
                    {t.name && <p className="font-medium text-foreground">{t.name}</p>}
                    <p className={t.name ? "text-xs text-muted-foreground" : "font-medium text-foreground"} data-testid={`testimonial-role-${i}`}>{t.role}</p>
                  </span>
                </figcaption>
              </motion.figure>
            ))}
          </div>
        </section>

        <section id="booking" className="mt-16 grid gap-8 lg:grid-cols-2 lg:items-center" data-testid="booking-section">
          <div>
            <p className="flex items-center gap-2 font-mono text-xs uppercase tracking-widest text-primary"><CalendarCheck size={15} /> One-to-one</p>
            <h2 className="mt-3 font-serif text-3xl">Book a 1:1 with JT</h2>
            <p className="mt-3 text-muted-foreground">
              Want personal guidance alongside the courses? Tell me a little about you and what you'd like to achieve,
              and I'll be in touch to arrange a private session — in person or online.
            </p>
            <div className="mt-6 overflow-hidden rounded-3xl border border-border">
              <img src="/media/jt-piano-lessons.jpg" alt="Jason Thompson playing piano" className="h-56 w-full object-cover sm:h-64" data-testid="booking-piano-image" />
            </div>
          </div>
          <BookingForm />
        </section>

        <div className="mt-16 flex flex-col items-start justify-between gap-4 rounded-3xl bg-secondary p-8 sm:flex-row sm:items-center">
          <div>
            <p className="flex items-center gap-2 font-mono text-xs uppercase tracking-widest text-primary"><GraduationCap size={15} /> Learn with me</p>
            <p className="mt-2 font-serif text-2xl text-foreground">Ready to read music with confidence?</p>
          </div>
          <div className="flex flex-col items-start gap-2 sm:items-center">
            <Link to="/courses" data-testid="about-final-cta">
              <Button className="rounded-full bg-primary px-6 py-6 text-foreground hover:bg-primary/90">Explore the courses <ArrowRight size={16} className="ml-2" /></Button>
            </Link>
            <p className="text-sm font-semibold text-muted-foreground" data-testid="about-final-cta-menu-hint">or click on the Menu tab for more information</p>
          </div>
        </div>
      </section>
    </div>
  );
}
